import { uniqueId } from 'lodash';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { confirm } from '@dashboard/lib/confirm';

import { apiUrl } from '@dashboard/services/env';
import logException from '@dashboard/services/logException';
import request from '@dashboard/services/request';

import useProduct from '../../useProduct';

import { useVariantEdit } from './VariantEditContext';
import { useVariants } from './VariantsContext';

function useVariantHelpers(variantId) {
  const { token, sellerId } = useAuth();
  const { onChange } = useVariantEdit();

  const { product } = useProduct();

  const { state: variants, dispatch } = useVariants();
  const { setIsAddingVariant, setHasUnsavedChanges, hasUnsavedChanges } = useVariantEdit();

  const variant = variants.find(variant => variant._id === variantId || variant.key === variantId);

  async function onCancelAddVariant() {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    dispatch({
      type: 'delete',
      variant,
    });
    setHasUnsavedChanges(false);
    setIsAddingVariant(false);
  }

  async function onDeleteVariant() {
    if (variant.isNew) {
      await onCancelAddVariant();
      return;
    }

    const del = await confirm('Delete this variant?', {
      confirm: 'Delete',
      ...(variant.options.some(option => option.files?.length > 0) && {
        description:
          'Past customers might lose access to attached files. Disable this variant to avoid this.',
      }),
    });

    if (!del) return;

    try {
      if (!variant._id) {
        dispatch({ type: 'delete', variant });
        return;
      }

      const response = await request(
        `${apiUrl}/sellers/${sellerId}/products/${product._id}/variants/${variant._id}`,
        token,
        {
          method: 'DELETE',
        },
      );

      if (response === true) {
        dispatch({ type: 'delete', variant });
        setHasUnsavedChanges(false);
        onChange();
      } else {
        throw new Error();
      }
    } catch (error) {
      logException(error);
      window.alert('Failed to delete variant. Try again or contact support');
    }
  }

  function onDuplicateVariant() {
    const preselectedOptionIndex = variant.options.findIndex(
      option => option._id === variant.preselect?.optionId,
    );

    const duplicatedOptions = variant.options.map(option => {
      delete option._id;
      delete option.id;
      delete option.stripeProductId;
      delete option.stripePriceId;

      return {
        ...option,
        key: uniqueId(),
      };
    });

    dispatch({
      type: 'add',
      values: [
        {
          ...variant,
          _id: undefined,
          status: 'enabled',
          name: `${variant.name} (copy)`,
          reference: undefined,
          key: uniqueId(),
          options: duplicatedOptions,
          preselect: {
            ...variant.preselect,
            optionId:
              preselectedOptionIndex > -1
                ? duplicatedOptions[preselectedOptionIndex].key
                : undefined,
          },
          isEditing: true,
          isNew: true,
        },
      ],
      index: variants.indexOf(variant) + 1,
    });

    setIsAddingVariant(true);
    setHasUnsavedChanges(true);
  }

  async function onEnableDisableVariant() {
    const status = variant.status === 'enabled' ? 'disabled' : 'enabled';

    if (!variant._id) {
      dispatch({
        type: 'update',
        variant,
        values: {
          ...variant,
          status,
        },
      });
      return;
    }

    const response = await request(
      `${apiUrl}/sellers/${sellerId}/products/${product._id}/variants/${variant._id}/status`,
      token,
      {
        method: 'PUT',
        body: JSON.stringify({
          status,
        }),
      },
    );

    if (response.data) {
      dispatch({
        type: 'update',
        variant,
        values: {
          ...variant,
          status,
        },
      });

      onChange();
    } else {
      throw new Error();
    }
  }

  function onSaveVariant() {
    if (variant.isNew) {
      setIsAddingVariant(false);
    }

    onChange();
  }

  function onDirty(dirty) {
    if (dirty && variant.isEditing) {
      setHasUnsavedChanges(true);
    }

    if (!dirty) {
      setHasUnsavedChanges(false);
    }
  }

  return {
    onCancelAddVariant,
    onDeleteVariant,
    onDuplicateVariant,
    onEnableDisableVariant,
    onSaveVariant,
    onDirty,
  };
}

export { useVariantHelpers };
